/**
 * Helpers for reading Purchase Order field values from a node.
 */

import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { formatLocationPostalValue } from 'utilities/Location';
import { coerceToDateTimeRangeValue, normalizeDateValue } from 'utilities/DateTime';

export const PURCHASE_ORDER_TYPE_KEY = 'PURCHASE_ORDER';

function normalizeString(value) {
  return String(value || '').trim();
}

function resolvePurchaseOrderTypeKey(node, nodeType = null) {
  return normalizeString(nodeType?.key || node?.type).toUpperCase() || PURCHASE_ORDER_TYPE_KEY;
}

/**
 * @param {object|null|undefined} node
 * @param {string} fieldName
 * @param {object|null|undefined} [nodeType]
 * @returns {unknown}
 */
export function readPurchaseOrderField(node, fieldName, nodeType = null) {
  const typeKey = resolvePurchaseOrderTypeKey(node, nodeType);
  return readNodeFieldRawValue(
    node,
    { name: fieldName, source: typeKey.toLowerCase() },
    typeKey,
  );
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatPurchaseOrderRelationLabel(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    return normalizeString(value);
  }

  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => formatPurchaseOrderRelationLabel(entry))
      .filter(Boolean);
    if (labels.length) return labels.join(', ');
    return value.length ? `${value.length} selected` : '';
  }

  if (typeof value === 'object') {
    return normalizeString(
      value.title
      || value.name
      || value.label
      || value.displayName
      || value.key,
    );
  }

  return normalizeString(value);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatPurchaseOrderDate(value) {
  const range = coerceToDateTimeRangeValue(value);
  const normalized = normalizeDateValue(range?.startDate ?? value);
  if (!normalized) {
    if (value == null || value === '') return '';
    return normalizeString(value);
  }

  try {
    const date = normalized instanceof Date ? normalized : new Date(normalized);
    if (Number.isNaN(date.getTime())) return normalizeString(value);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return normalizeString(value);
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatPurchaseOrderEnum(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    return normalizeString(value.label || value.title || value.value || value.key);
  }
  const raw = normalizeString(value);
  if (!raw) return '';
  if (!raw.includes('_') && !raw.includes('-')) return raw;
  return raw
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * @param {unknown} locationValue
 * @returns {string}
 */
export function formatPurchaseOrderAddress(locationValue) {
  return formatLocationPostalValue(locationValue) || '';
}

/**
 * @param {object|null|undefined} node
 * @param {object|null|undefined} [nodeType]
 * @returns {{
 *   title: string,
 *   number: string,
 *   paymentTerms: string,
 *   billedDate: string,
 *   sendDate: string,
 *   dueDate: string,
 *   vendorLabel: string,
 *   shipToLabel: string,
 *   vendorAddress: string,
 *   shipToAddress: string,
 *   shipToEmail: string,
 *   shipToPhone: string,
 * }}
 */
export function getPurchaseOrderGeneralInfoModel(node, nodeType = null) {
  const vendor = readPurchaseOrderField(node, 'vendor', nodeType);
  const shipTo = readPurchaseOrderField(node, 'shipTo', nodeType)
    || readPurchaseOrderField(node, 'client', nodeType);

  const vendorAddress = typeof vendor === 'object' && !Array.isArray(vendor)
    ? formatPurchaseOrderAddress(vendor.address || vendor.location)
    : '';
  const shipToAddress = typeof shipTo === 'object' && !Array.isArray(shipTo)
    ? formatPurchaseOrderAddress(shipTo.address || shipTo.location)
    : '';
  const shipToEmail = typeof shipTo === 'object' && !Array.isArray(shipTo)
    ? normalizeString(shipTo.email)
    : '';
  const shipToPhone = typeof shipTo === 'object' && !Array.isArray(shipTo)
    ? normalizeString(shipTo.phone)
    : '';

  return {
    title: normalizeString(node?.title) || 'Untitled purchase order',
    number: normalizeString(
      readPurchaseOrderField(node, 'number', nodeType)
      || readPurchaseOrderField(node, 'poNumber', nodeType),
    ),
    paymentTerms: formatPurchaseOrderEnum(readPurchaseOrderField(node, 'paymentTerms', nodeType)),
    billedDate: formatPurchaseOrderDate(readPurchaseOrderField(node, 'billedDate', nodeType)),
    sendDate: formatPurchaseOrderDate(readPurchaseOrderField(node, 'sendDate', nodeType)),
    dueDate: formatPurchaseOrderDate(readPurchaseOrderField(node, 'dueDate', nodeType)),
    vendorLabel: formatPurchaseOrderRelationLabel(vendor),
    shipToLabel: formatPurchaseOrderRelationLabel(shipTo),
    vendorAddress,
    shipToAddress,
    shipToEmail,
    shipToPhone,
  };
}
